import React, { useState, useCallback } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  TextField,
  InputAdornment,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddCircleOutline } from "@mui/icons-material";
import { Search } from "lucide-react";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import Header from "../components/Admin/header/Header";
import CloseIcon from "@mui/icons-material/Close";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import { EditIcon } from "../comps/CustomIcons";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_LIMIT,
  DEFAULT_ROWS_PER_PAGE_OPTIONS,
} from "../utils/constants";
import CityModal from "../components/item_master/modal/city-modal";
import { useGetApi } from "../hooks/useGetApi";
import { deleteCityByIdApi, getCityApi } from "../services/city.service";
import { toast } from "react-toastify";
import Loader from "../components/Loader/loader";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const searchByOptions = [
  { value: "cityName", label: "City Name" },
  { value: "stateName", label: "State Name" },
  { value: "countryName", label: "Country" },
];

export default function MasterCity() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [search, setSearch] = useState("");
  const [searchBy, setSearchBy] = useState("cityName");
  const [selectedRow, setSelectedRow] = useState(null);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: DEFAULT_LIMIT,
  });
  const [sortModel, setSortModel] = useState([{ field: "cityName", sort: "asc" }]);
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const searchByLabel =
    searchByOptions.find((o) => o.value === searchBy)?.label || "City Name";

  const {
    dataList: cityList,
    dataCount: cityCount,
    isLoading,
    isError,
    refetch,
  } = useGetApi({
    apiFunction: getCityApi,
    params: {
      limit: paginationModel.pageSize,
      page: paginationModel.page + 1,
      search,
      searchBy,
      sort: sortModel[0]?.field || "cityName",
      order: sortModel[0]?.sort || "asc",
    },
    dependencies: [
      search,
      searchBy,
      paginationModel.pageSize,
      paginationModel.page,
      sortModel,
    ],
    debounceDelay: 500,
    dataKey: "cities",
    countKey: "total",
  });

  const rawRows =
    cityList?.map((row) => ({
      id: row._id,
      _id: row._id,
      cityName: row?.cityName || "-",
      stateName: row?.state?.stateName || "-",
      countryName: row?.country?.countryName || "-",
      status: row?.active ? "Active" : "Inactive",
      _original: row,
    })) || [];

  const sortField = sortModel[0]?.field || "cityName";
  const sortOrder = sortModel[0]?.sort || "asc";
  const sortedRows = [...rawRows].sort((a, b) => {
    const cmp = String(a[sortField] ?? "").localeCompare(
      String(b[sortField] ?? ""),
      undefined,
      { numeric: true }
    );
    return sortOrder === "asc" ? cmp : -cmp;
  });
  const pageSize = paginationModel.pageSize;
  const page = paginationModel.page;
  const rows =
    sortedRows.length > pageSize
      ? sortedRows.slice(page * pageSize, page * pageSize + pageSize)
      : sortedRows;

  const handleModalOpen = () => setModalOpen(true);
  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedRow(null);
  };
  const handleEdit = (rowData) => {
    setSelectedRow(rowData);
    handleModalOpen();
  };
  const handleDelete = async (rowId) => {
    setLoading(true);
    const response = await deleteCityByIdApi(rowId);
    setLoading(false);
    if (response?.code === 200) {
      toast.success(response?.message || "City deleted successfully");
      refetch();
    } else {
      toast.error(response?.message || "Some error occurred.");
    }
  };

  const getColumns = useCallback(
    () => [
      {
        field: "cityName",
        headerName: "City Name",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "stateName",
        headerName: "State Name",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "countryName",
        headerName: "Country",
        flex: 1,
        minWidth: 120,
        headerAlign: "left",
        align: "left",
        sortable: true,
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        minWidth: 90,
        headerAlign: "center",
        align: "center",
        sortable: true,
        renderCell: (params) => (
          <Chip
            label={params.value || "Active"}
            color={params.value === "Inactive" ? "error" : "success"}
            size="small"
            variant="outlined"
          />
        ),
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0,
        width: 140,
        minWidth: 120,
        headerAlign: "right",
        align: "right",
        sortable: false,
        renderCell: (params) => (
          <Box sx={{ display: "flex", gap: 0.5, justifyContent: "flex-end" }}>
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row._original)}
            >
              <EditIcon
                sx={{
                  cursor: "pointer",
                  color: "transparent",
                  fontSize: "18px",
                }}
              />
            </IconButton>
            <IconButton size="small">
              <RemoveRedEyeOutlinedIcon sx={{ fontSize: "18px" }} />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(params.row._id)}
            >
              <Tooltip title="Delete City">
                <CloseIcon sx={{ fontSize: "18px" }} />
              </Tooltip>
            </IconButton>
          </Box>
        ),
      },
    ],
    []
  );

  const dataGridSx = {
    border: "none",
    "& .MuiDataGrid-root": { border: "none", width: "100%" },
    "& .MuiDataGrid-cell": {
      borderBottom: "1px solid #f0f0f0",
      padding: isMobile ? "4px" : "8px",
      fontSize: isMobile ? "12px" : "14px",
    },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "#f5f5f5",
      borderBottom: "2px solid #e0e0e0",
    },
    "& .MuiDataGrid-columnHeader": {
      backgroundColor: "#f5f5f5",
      fontWeight: "bold",
    },
    "& .MuiDataGrid-row": {
      "&:hover": { backgroundColor: "#f5f5f5" },
      "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
      "&:nth-of-type(even)": { backgroundColor: "white" },
    },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "2px solid #e0e0e0",
      backgroundColor: "#f9f9f9",
    },
  };

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px", paddingBottom: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: "#5D6A82",
              textTransform: "none",
              fontSize: "16px",
              fontWeight: "bold",
              padding: 0,
              transition: "none !important",
              mt: 2,
            }}
          >
            Back
          </Button>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: 2,
            }}
          >
            <Typography sx={{ color: "rgba(93, 106, 130, 1)", fontSize: 20 }}>
              City Master
            </Typography>
          </Box>
          <Box
            sx={{
              my: "20px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              paddingBottom: 2,
            }}
          >
            <TextField
              placeholder={`Search by ${searchByLabel}`}
              size="small"
              sx={{ width: "300px" }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <select
                        className="uid-select"
                        value={searchBy}
                        onChange={(e) => setSearchBy(e.target.value)}
                        style={{
                          border: "none",
                          outline: "none",
                          background: "transparent",
                          cursor: "pointer",
                          color: "#333",
                          fontWeight: "500",
                        }}
                      >
                        {searchByOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                      <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
                      <Search />
                    </InputAdornment>
                  ),
                },
              }}
              value={search || ""}
              onChange={(e) => setSearch(e.target.value || "")}
            />
            <Button
              variant="contained"
              sx={{
                textTransform: "none",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                minWidth: "150px",
              }}
              onClick={handleModalOpen}
            >
              <AddCircleOutline />
              <Typography sx={{ color: "white" }}>New City</Typography>
            </Button>
          </Box>

          {isLoading || loading ? (
            <Loader />
          ) : isError ? (
            <ErrorMessage />
          ) : (
            <Box
              sx={{
                height: {
                  xs: "calc(100vh - 300px)",
                  sm: "calc(100vh - 320px)",
                  md: "calc(100vh - 350px)",
                },
                width: "100%",
                minHeight: "300px",
                overflow: "hidden",
              }}
            >
              <DataGrid
                rows={rows}
                columns={getColumns()}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                paginationMode="server"
                rowCount={cityCount ?? 0}
                sortModel={sortModel}
                onSortModelChange={setSortModel}
                sortingMode="server"
                disableRowSelectionOnClick
                loading={isLoading}
                sx={dataGridSx}
                components={{
                  NoRowsOverlay: () => (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No cities found
                      </Typography>
                    </Box>
                  ),
                }}
              />
            </Box>
          )}
        </Box>
        <CityModal
          open={modalOpen}
          onClose={handleModalClose}
          refetch={refetch}
          selectedRow={selectedRow}
        />
      </Box>
    </Container>
  );
}
